import { Box } from 'src/design/atoms/Box';
import { Group } from 'src/design/atoms/Group';
import { Paper } from 'src/design/atoms/Paper';
import { Stack } from 'src/design/atoms/Stack';
import { Text } from 'src/design/atoms/Text';
import { IconBrain } from '@tabler/icons-react';
import React from 'react';
import { ChatMemoryDto } from 'src/api/FiretrolApiClient';
import { ChatMemoryAction } from 'src/api/queries/chat/useChatConversationQueries';
import { useChatMemoryQuery } from 'src/api/queries/chat/useChatMemoryQueries';
import {
  useAcceptChatMemory,
  useDeclineChatMemory,
  useDeleteChatMemory,
} from 'src/api/queries/chat/useChatMemoryMutations';
import { MEMORY_PROFILE_PATH, memoryKindLabel } from 'src/app/chat/memoryNotes';
import { BasicButton } from 'src/design/atoms/BasicButton';
import { SecondaryButton } from 'src/design/atoms/SecondaryButton';
import { AnchorLink } from 'src/design/molecules/AnchorLink';
import { useChatUi } from 'src/hooks/useChat';

export interface IMemoryCardProps {
  /** The note as the reply left it; its current state comes from the user's memory list. */
  memory: ChatMemoryDto;
  /** A note the reply removed always shows as removed, without reading the memory list. */
  action?: ChatMemoryAction;
  /** Shows the snapshot with no actions and without reading the memory list (admin transcript view). */
  readOnly?: boolean;
}

type MemoryCardState = 'proposed' | 'saved' | 'declined' | 'removed' | 'gone';

const HEADINGS: Record<MemoryCardState, string> = {
  proposed: 'F.I.R.E. suggests remembering this',
  saved: 'Saved to memory',
  declined: 'Not saved',
  removed: 'Removed from memory',
  gone: 'No longer in memory',
};

const stateOf = (
  note: ChatMemoryDto | null,
  snapshot: ChatMemoryDto,
): MemoryCardState => {
  // A missing note whose snapshot was a suggestion may have been declined and
  // cleaned up, or accepted and later deleted; the card cannot tell which.
  if (!note) return snapshot.status === 'active' ? 'removed' : 'gone';
  if (note.status === 'proposed') return 'proposed';
  if (note.status === 'declined') return 'declined';
  return 'saved';
};

/** @registry Card for a memory note an assistant reply saved, suggested, or removed: reads the note's live state from the user's memory list (Save / No thanks for a suggestion, Undo and Manage for a saved note, muted Not saved / Removed from memory / No longer in memory otherwise); readOnly shows the stored snapshot with no actions. */
export const MemoryCard: React.FC<IMemoryCardProps> = ({
  memory,
  action = 'saved',
  readOnly = false,
}) => {
  const { closeDrawer } = useChatUi();
  const removedByReply = action === 'removed';
  const memoryQuery = useChatMemoryQuery(!readOnly && !removedByReply);
  const accept = useAcceptChatMemory();
  const decline = useDeclineChatMemory();
  const remove = useDeleteChatMemory();

  const live = memoryQuery.data?.notes.find((note) => note.id === memory.id);
  // A note missing from a list that is still loading or refetching may just
  // have been saved, so the snapshot stands in until the list settles.
  const settled = !!memoryQuery.data && !memoryQuery.isFetching;
  const note = readOnly ? memory : live ?? (settled ? null : memory);
  const state = removedByReply ? 'removed' : stateOf(note, memory);
  const muted = state === 'declined' || state === 'removed' || state === 'gone';
  const pending = accept.isPending || decline.isPending || remove.isPending;

  return (
    <Paper
      p="xs"
      radius="md"
      withBorder
      data-testid="memory-card"
      data-state={state}
    >
      <Group gap="xs" wrap="nowrap" align="flex-start">
        <Box
          c={muted ? 'dimmed' : 'blue.7'}
          style={{ display: 'flex', flexShrink: 0, marginTop: 2 }}
        >
          <IconBrain size={16} aria-hidden />
        </Box>
        <Stack gap={4} style={{ minWidth: 0, flex: 1 }}>
          <Text size="xs" fw={600} c={muted ? 'dimmed' : 'bright'}>
            {HEADINGS[state]} · {memoryKindLabel(memory.kind)}
          </Text>
          <Text size="sm" c={muted ? 'dimmed' : 'bright'}>
            {note?.text ?? memory.text}
          </Text>
          {!readOnly && !muted && (
            <Group gap="xs">
              {state === 'proposed' && (
                <>
                  <BasicButton
                    size="compact-xs"
                    loading={accept.isPending}
                    disabled={pending}
                    onClick={() => accept.mutate(memory.id)}
                  >
                    Save
                  </BasicButton>
                  <SecondaryButton
                    size="compact-xs"
                    loading={decline.isPending}
                    disabled={pending}
                    onClick={() => decline.mutate(memory.id)}
                  >
                    No thanks
                  </SecondaryButton>
                </>
              )}
              {state === 'saved' && note?.source === 'user_request' && (
                <SecondaryButton
                  size="compact-xs"
                  loading={remove.isPending}
                  disabled={pending}
                  onClick={() => remove.mutate(memory.id)}
                >
                  Undo
                </SecondaryButton>
              )}
              {state === 'saved' && (
                <AnchorLink
                  to={MEMORY_PROFILE_PATH}
                  size="xs"
                  onClick={closeDrawer}
                >
                  Manage
                </AnchorLink>
              )}
            </Group>
          )}
        </Stack>
      </Group>
    </Paper>
  );
};
